// recommendationImpactCalculator.ts
// Implements Big 6 recalculation based on recommendation impacts
// Uses Excel SUMIF formula logic while maintaining simulation-only changes

export interface Question360 {
  questionId: number;
  questionText: string;
  currentScore: number; // 1-5 scale
  originalScore: number; // baseline from assessment
  category: string; // maps to Big 6 categories
  weight: number; // from Excel weighting matrix
  isIncluded: boolean; // SUMIF "x" marker equivalent
}

export interface CategoryMapping {
  category: string; // governance, techDebt, culture, thirdParty, budget, people
  questions: Question360[];
  excelFormula: string; // e.g., "sumif(B32:B87, 'x', D32:D87)"
}

export interface RecommendationImpact {
  questionId: number;
  scoreImprovement: number; // How much the 1-5 score improves
}

export interface SimulationResults {
  original360Scores: { [questionId: number]: number };
  updated360Scores: { [questionId: number]: number };
  originalBig6: { [category: string]: number };
  updatedBig6: { [category: string]: number };
  improvements: { [category: string]: number };
  isSimulation: true;
}

/**
 * Recommendation Impact Calculator
 * Handles dynamic recalculation of Big 6 scores based on recommendation impacts
 */
export class RecommendationImpactCalculator {
  private categoryMappings: CategoryMapping[] = [];
  private baselineFormula: any = null;

  /**
   * Initialize with current assessment formula and mappings
   */
  async initialize(assessmentId: string, realScores?: { [key: string]: number }): Promise<void> {
    console.log('🔧 RecommendationImpactCalculator initializing for assessment:', assessmentId);
    console.log('📊 Real scores provided:', realScores);
    
    // Try to load real data first
    try {
      // Attempt to load real assessment formula and mappings
      this.baselineFormula = await this.loadAssessmentFormula(assessmentId);
      this.categoryMappings = await this.loadCategoryMappings(assessmentId);
      
      if (this.categoryMappings && this.categoryMappings.length > 0) {
        console.log('✅ Loaded real assessment data with', this.categoryMappings.length, 'category mappings');
        
        // If we have real scores, update the baseline scores in our mappings
        if (realScores) {
          console.log('📊 Updating baseline with real assessment scores:', realScores);
          this.updateBaselineScores(realScores);
        }
        return;
      }
    } catch (error) {
      console.log('⚠️ Could not load real assessment data:', error);
    }
    
    // Fall back to mock data if real data isn't available
    console.warn('⚠️ Using mock data - real 360 question mappings not available');
    this.initializeMockData();
    
    // Update mock data with real scores if provided
    if (realScores) {
      console.log('📊 Updating mock baseline with real assessment scores:', realScores);
      this.updateBaselineScores(realScores);
    }
  }

  /**
   * Apply recommendation impacts and recalculate Big 6 scores
   */
  applyRecommendationImpacts(
    checkedRecommendations: any[],
    baselineScores: { [category: string]: number }
  ): SimulationResults {
    
    // Step 1: Collect all 360 question impacts from checked recommendations
    const all360Impacts: RecommendationImpact[] = [];
    
    checkedRecommendations.forEach(rec => {
      if (rec.affects360) {
        all360Impacts.push(...rec.affects360);
      }
    });

    // Step 2: Update 360 scores with improvements (1-5 scale)
    const updated360Scores: { [questionId: number]: number } = {};
    
    this.getAllQuestions().forEach(question => {
      let newScore = question.originalScore;
      
      // Apply all impacts that affect this question
      all360Impacts
        .filter(impact => impact.questionId === question.questionId)
        .forEach(impact => {
          newScore = Math.min(5, newScore + impact.scoreImprovement);
        });
      
      updated360Scores[question.questionId] = newScore;
    });

    // Step 3: Recalculate Big 6 using Excel SUMIF formula
    // IMPORTANT: These are RAW SCORES that can exceed 100, not percentages!
    // OPTIMIZATION: Move toward Industry Standard/Best-in-Class (not just lower)
    // - Too HIGH = Under-optimized (not enough security)
    // - Too LOW = Over-optimized (excessive security, wasting resources)
    // - JUST RIGHT = Optimal zone (Industry Standard to Best-in-Class range)
    const updatedBig6: { [category: string]: number } = {};
    
    this.categoryMappings.forEach(mapping => {
      let totalScore = 0;
      let includedCount = 0;

      // Excel SUMIF: sum values where marker column = "x" (included = true)
      mapping.questions.forEach(question => {
        if (question.isIncluded) {
          const updatedScore = updated360Scores[question.questionId] || question.originalScore;
          const weightedValue = updatedScore * question.weight;
          totalScore += weightedValue;
          includedCount++;
        }
      });

      console.log(`📊 ${mapping.category}: Raw SUMIF score = ${totalScore} from ${includedCount} questions`);

      // Keep raw SUMIF score - this matches Excel's actual calculation
      updatedBig6[mapping.category] = totalScore;
    });

    // Step 4: Map category names (keep raw scores, don't convert to percentage)
    const mappedBig6: { [category: string]: number } = {};
    Object.entries(updatedBig6).forEach(([category, rawScore]) => {
      // Just map the category name, keep the raw score
      const mappedCategory = this.mapCategoryName(category);
      mappedBig6[mappedCategory] = Math.round(rawScore);
      
      console.log(`📊 ${mappedCategory}: Raw score = ${Math.round(rawScore)}`);
    });

    // Step 5: Calculate changes (moving toward optimal zone)
    const improvements: { [category: string]: number } = {};
    Object.keys(baselineScores).forEach(category => {
      if (mappedBig6[category] !== undefined) {
        // Change in score (negative = decreased, positive = increased)
        // Whether this is "good" depends on if we're moving toward optimal zone
        improvements[category] = mappedBig6[category] - baselineScores[category];
      }
    });

    console.log('📈 Big 6 Optimization Results:');
    console.log('Original Raw Scores:', baselineScores);
    console.log('Updated Raw Scores:', mappedBig6);
    console.log('Score Changes (toward optimal):', improvements);
    console.log('Note: Optimal direction depends on starting position vs Industry Standard');

    return {
      original360Scores: this.getOriginal360Scores(),
      updated360Scores,
      originalBig6: baselineScores,
      updatedBig6: mappedBig6,
      improvements,
      isSimulation: true
    };
  }

  /**
   * Load assessment formula and 360 mappings from backend
   */
  private async loadAssessmentFormula(assessmentId: string): Promise<any> {
    try {
      // Use proper API base URL for development vs production
      const getApiBaseUrl = () => {
        // When running in Docker, use relative URLs (nginx proxy handles routing)
        // When running locally on localhost, use direct backend URL
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          return import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5001' : '');
        }
        return ''; // Use relative URLs - Docker/nginx will proxy to backend
      };
      
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/recommendations/assessment-formula/${assessmentId}`);
      if (response.ok) {
        const result = await response.json();
        // Handle API response format: { success: true, data: {...} }
        return result.success && result.data ? result.data : result;
      }
    } catch (error) {
      console.log('⚠️ Could not load assessment formula, using mock data');
    }
    return null;
  }

  /**
   * Load category to question mappings
   */
  private async loadCategoryMappings(assessmentId: string): Promise<CategoryMapping[]> {
    try {
      // Use proper API base URL for development vs production
      const getApiBaseUrl = () => {
        // When running in Docker, use relative URLs (nginx proxy handles routing)
        // When running locally on localhost, use direct backend URL
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          return import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5001' : '');
        }
        return ''; // Use relative URLs - Docker/nginx will proxy to backend
      };
      
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/recommendations/category-mappings/${assessmentId}`);
      if (response.ok) {
        const result = await response.json();
        // Handle API response format: { success: true, data: [...] }
        return result.success && result.data ? result.data : result;
      }
    } catch (error) {
      console.log('⚠️ Could not load category mappings, using mock data');
    }
    return this.getMockCategoryMappings();
  }

  /**
   * Initialize with mock data for development
   */
  private initializeMockData(): void {
    this.categoryMappings = this.getMockCategoryMappings();
  }

  /**
   * Get all questions across all categories
   */
  private getAllQuestions(): Question360[] {
    console.log('🔍 Getting all questions, categoryMappings:', this.categoryMappings);
    console.log('🔍 categoryMappings type:', typeof this.categoryMappings);
    console.log('🔍 categoryMappings isArray:', Array.isArray(this.categoryMappings));
    
    if (!this.categoryMappings || !Array.isArray(this.categoryMappings)) {
      console.warn('⚠️ categoryMappings is not an array, using mock data');
      this.categoryMappings = this.getMockCategoryMappings();
    }
    
    return this.categoryMappings.flatMap(mapping => mapping.questions || []);
  }

  /**
   * Get original 360 scores
   */
  private getOriginal360Scores(): { [questionId: number]: number } {
    const scores: { [questionId: number]: number } = {};
    this.getAllQuestions().forEach(q => {
      scores[q.questionId] = q.originalScore;
    });
    return scores;
  }

  /**
   * Update baseline scores with real assessment data
   */
  private updateBaselineScores(realScores: { [key: string]: number }): void {
    // This method updates the mock or real category mappings with actual baseline scores
    // The scores would typically be used to set more realistic baseline values
    // For now, we'll just log them as the mock data structure doesn't directly use these
    console.log('📊 Real baseline scores available for impact calculations:', realScores);
    
    // Store the real scores for reference
    if (this.categoryMappings && this.categoryMappings.length > 0) {
      // If we have mappings, we could update question scores here
      // This would require a more complex data structure to map questions to categories
      console.log('🔄 Real scores stored for baseline reference');
    }
  }
  
  /**
   * Map category names from impact calculator format to Goldilocks format
   */
  private mapCategoryName(category: string): string {
    const categoryMap: { [key: string]: string } = {
      'technicalDebt': 'technical_debt',
      'thirdParty': 'third_party',
      'governance': 'governance',
      'culture': 'culture', 
      'budget': 'budget',
      'people': 'people'
    };
    
    return categoryMap[category] || category;
  }

  /**
   * Mock category mappings for development
   */
  private getMockCategoryMappings(): CategoryMapping[] {
    return [
      {
        category: 'governance',
        excelFormula: 'sumif(B32:B87, "x", D32:D87)',
        questions: [
          {
            questionId: 101,
            questionText: 'Does your organization have formal security governance?',
            currentScore: 2,
            originalScore: 2,
            category: 'governance',
            weight: 1.0,
            isIncluded: true
          },
          {
            questionId: 102,
            questionText: 'Are security policies regularly reviewed?',
            currentScore: 3,
            originalScore: 3,
            category: 'governance',
            weight: 1.2,
            isIncluded: true
          }
        ]
      },
      {
        category: 'culture',
        excelFormula: 'sumif(H32:H87, "x", J32:J87)',
        questions: [
          {
            questionId: 150,
            questionText: 'Do employees receive regular security training?',
            currentScore: 2,
            originalScore: 2,
            category: 'culture',
            weight: 1.0,
            isIncluded: true
          }
        ]
      },
      {
        category: 'thirdParty',
        excelFormula: 'sumif(K32:K87, "x", M32:M87)',
        questions: [
          {
            questionId: 201,
            questionText: 'Do you have a formal vendor risk assessment process?',
            currentScore: 2,
            originalScore: 2,
            category: 'thirdParty',
            weight: 1.5,
            isIncluded: true
          }
        ]
      },
      {
        category: 'people',
        excelFormula: 'sumif(Q32:Q87, "x", S32:S87)',
        questions: [
          {
            questionId: 250,
            questionText: 'Are security roles and responsibilities clearly defined?',
            currentScore: 2,
            originalScore: 2,
            category: 'people',
            weight: 1.0,
            isIncluded: true
          }
        ]
      },
      {
        category: 'technicalDebt',
        excelFormula: 'sumif(E32:E87, "x", G32:G87)',
        questions: [
          {
            questionId: 300,
            questionText: 'Are systems regularly updated and patched?',
            currentScore: 3,
            originalScore: 3,
            category: 'technicalDebt',
            weight: 1.0,
            isIncluded: true
          }
        ]
      },
      {
        category: 'budget',
        excelFormula: 'sumif(N32:N87, "x", P32:P87)',
        questions: [
          {
            questionId: 350,
            questionText: 'Is there adequate budget for security initiatives?',
            currentScore: 3,
            originalScore: 3,
            category: 'budget',
            weight: 1.0,
            isIncluded: true
          }
        ]
      }
    ];
  }
}

// Export singleton instance
export const impactCalculator = new RecommendationImpactCalculator();